import type {
  Incident,
  IncidentComment,
  ApiResponse,
  StatusPanelSettings,
  OperatorUser,
  GroupConfig,
  GroupMember,
  NotificationCapabilities,
  DiscoverableGroup,
} from 'types';

const API_BASE = '/api';

// ─── Token Provider ───
type TokenProvider = () => Promise<string | null>;
let authTokenProvider: TokenProvider | null = null;

/**
 * Set the token provider for API authentication.
 * Called once during app initialization with the auth context's getAccessToken.
 */
export function setAuthTokenProvider(provider: TokenProvider): void {
  authTokenProvider = provider;
}

/**
 * Error thrown by `request` when the API returns a non-2xx response.
 * Exposes the parsed `ApiResponse<unknown>` envelope when available so callers
 * can read a structured server-side error message without re-parsing JSON.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiResponse<unknown> | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract a user-facing message from an unknown error.
 * Prefers the `error` field of an `ApiResponse` envelope (returned by `request`
 * via `ApiError`) and falls back to `Error.message` or `fallback`.
 */
export function extractApiErrorMessage(err: unknown, fallback = 'An unknown error occurred'): string {
  if (err instanceof ApiError) {
    return err.body?.error ?? err.message ?? fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  // Attach bearer token if provider is set
  if (authTokenProvider) {
    const token = await authTokenProvider();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const text = await response.text();
    let body: ApiResponse<unknown> | null = null;
    try {
      body = text ? (JSON.parse(text) as ApiResponse<unknown>) : null;
    } catch {
      // Server returned non-JSON error (e.g. plain-text 5xx). Leave body null.
    }
    throw new ApiError(response.status, body, body?.error ?? text ?? `HTTP ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// ─── Status Panel API ───

export async function getActiveIncidents(): Promise<Incident[]> {
  return request<Incident[]>('/incidents/active');
}

export async function acknowledgeIncident(incidentId: string): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/acknowledge`, {
    method: 'POST',
  });
}

export async function takeoverIncident(
  incidentId: string,
  operatorId: string,
): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/takeover`, {
    method: 'POST',
    body: JSON.stringify({ operatorId }),
  });
}

export async function escalateIncident(incidentId: string): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/escalate`, {
    method: 'POST',
  });
}

export async function closeIncident(incidentId: string): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/close`, {
    method: 'POST',
  });
}

export async function updateIncidentSeverity(
  incidentId: string,
  severity: number,
): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/severity`, {
    method: 'POST',
    body: JSON.stringify({ severity }),
  });
}

export async function updateIncidentStatus(
  incidentId: string,
  status: 'Active' | 'Closed',
  classification?: 'Undetermined' | 'TruePositive' | 'BenignPositive' | 'FalsePositive',
  classificationReason?: 'SuspiciousActivity' | 'SuspiciousButExpected' | 'IncorrectAlertLogic' | 'InaccurateData',
  classificationComment?: string,
): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/status`, {
    method: 'POST',
    body: JSON.stringify({
      status,
      classification,
      classificationReason,
      classificationComment,
    }),
  });
}

export async function getIncidentComments(incidentId: string): Promise<ApiResponse<IncidentComment[]>> {
  return request<ApiResponse<IncidentComment[]>>(`/incidents/${incidentId}/comments`);
}

export async function addIncidentComment(
  incidentId: string,
  message: string,
): Promise<ApiResponse<IncidentComment>> {
  return request<ApiResponse<IncidentComment>>(`/incidents/${incidentId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

// ─── Tags API ───

export async function updateIncidentTags(
  incidentId: string,
  isCritical: boolean,
  nis2Categories: string[],
): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>(`/incidents/${incidentId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ isCritical, nis2Categories }),
  });
}

// ─── Settings API ───

export async function getSettings(): Promise<StatusPanelSettings> {
  return request<StatusPanelSettings>('/settings');
}

export async function saveSettings(settings: StatusPanelSettings): Promise<ApiResponse<StatusPanelSettings>> {
  return request<ApiResponse<StatusPanelSettings>>('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ─── Test API ───

export async function createTestIncident(severity: string = 'High'): Promise<ApiResponse<Incident>> {
  return request<ApiResponse<Incident>>('/incidents/test/create', {
    method: 'POST',
    body: JSON.stringify({ severity }),
  });
}

// ─── User API ───

/**
 * Gets all users who are members of the SOC operators security group.
 * Used by admins to select a user when assigning incident ownership.
 * Requires admin role (soc-admin-asg membership).
 */
export async function getOperators(): Promise<OperatorUser[]> {
  return request<OperatorUser[]>('/users/operators');
}

// ─── Groups API ───

/**
 * Gets all configured SOC groups (Operators, Admins, etc.).
 */
export async function getGroups(): Promise<GroupConfig[]> {
  return request<GroupConfig[]>('/groups');
}

/**
 * Gets members of a specific SOC group.
 */
export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  return request<GroupMember[]>(`/groups/${encodeURIComponent(groupId)}/members`);
}

/**
 * Gets Azure AD groups visible to the current user via delegated Graph API.
 */
export async function getDiscoverableGroups(search?: string): Promise<DiscoverableGroup[]> {
  const query = search?.trim()
    ? `?search=${encodeURIComponent(search.trim())}`
    : '';

  return request<DiscoverableGroup[]>(`/groups/discover${query}`);
}

// ─── Notification API ───

/**
 * Gets notification capabilities indicating which channels are configured server-side.
 */
export async function getNotificationCapabilities(): Promise<NotificationCapabilities> {
  return request<NotificationCapabilities>('/settings/notification-capabilities');
}


